// Hero of the Collect product landing: the name, the tagline, what the product does in one
// sentence and the way in. The flow line names the product's three stages (the same
// colours as the background flow and the "How it works" cards).
import { motion } from "motion/react";
import { useLandingTheme } from "./theme";
import { LogIn, ArrowDown } from "lucide-react";
import { STAGES } from "./ProcessFlow";

export function HeroSection({ passSignIn, signInLabel, onSignIn }: { passSignIn: boolean; signInLabel: string; onSignIn: () => void }) {
  const { tone } = useLandingTheme();
  return (
    <section aria-labelledby="hero-title" className="relative min-h-screen px-6 md:px-12 pt-28 pb-16 flex flex-col justify-center">
      <div className="max-w-6xl mx-auto w-full">
        <motion.div
          // Transform only: the headline (LCP) is visible from the first paint.
          initial={{ y: 40 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 id="hero-title" className="text-7xl md:text-[10rem] leading-[0.85] font-serif font-light tracking-tight mb-10">
            Collect.
          </h1>
          <p className="text-2xl md:text-4xl font-serif font-light italic text-white/80 mb-12">
            Breathe Life Into Information.
          </p>
        </motion.div>

        {/* The product flow: documents → wiki → capsules */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-x-8 font-mono text-xs md:text-sm uppercase tracking-[0.2em]"
        >
          {STAGES.map((s, i) => (
            <span key={s.n} className="contents">
              {i > 0 && <span className="text-white/60" aria-hidden="true">→</span>}
              <span style={{ color: tone(s.color) }}>{s.name}</span>
            </span>
          ))}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-10 text-lg md:text-2xl font-light text-white/70 max-w-3xl leading-relaxed"
        >
          Collect turns your documents into a Markdown wiki — summaries, concept pages and a
          knowledge graph written by the LLM you choose — and sends the workspace to your
          HIVE as a compressed capsule whose SHA-256 hash is checked when you open it.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-12 flex flex-wrap items-center gap-3"
        >
          <button type="button" className="btn btn-primary" onClick={onSignIn}>
            <LogIn size={16} aria-hidden="true" />
            {signInLabel}
          </button>
          <a href="#how" className="btn btn-ghost">
            <ArrowDown size={16} aria-hidden="true" />
            How it works
          </a>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/70"
        >
          {passSignIn ? "Closed beta · sign in with your Ordinal Pass" : "Closed beta · access keys issued by Qodoonect"}
        </motion.p>
      </div>
    </section>
  );
}
